import React, { useMemo } from "react"
import styled from "~/ui/styled"
import {
  BetweenFlex,
  Button,
  CenterFlex,
  ColumnFlex,
  IconFont,
  StartFlex,
  Text,
} from "~components/El"
import { useTheme } from "~/ui/styled"
import { formatMessageTime } from "~utils"
import type { NoticeItem } from "./notice-center-context"

const Card = styled(ColumnFlex)`
  width: 100%;
  min-height: 0;
`

const CardBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 24px;
`

const CardFooter = styled(BetweenFlex)`
  flex-shrink: 0;
  padding: 16px 24px 20px;
  gap: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const InfoIcon = styled(CenterFlex)`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`

const TimeBox = styled(ColumnFlex)`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  background-color: #eef6ff;
  margin-top: 16px;
`

const NoticeBox = styled(ColumnFlex)`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  background-color: #fff8ef;
  margin-top: 16px;
`

const BulletRow = styled(StartFlex)`
  width: 100%;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
`

const DEFAULT_NOTICE =
  "维护期间可能无法正常使用部分功能，请提前做好相关准备。给您带来的不便，敬请谅解。"

function parseStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map((s) => s.trim()).filter(Boolean)
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\r?\n|[;；]/)
      .map((s) => s.replace(/^[-•·\d.)\s]+/, "").trim())
      .filter(Boolean)
  }
  return []
}

function parseMaintenanceTime(item: NoticeItem) {
  const extra = item.extra ?? {}
  if (extra.maintenance_time) return String(extra.maintenance_time)
  if (extra.time_range) return String(extra.time_range)
  if (extra.start_time && extra.end_time) {
    return `${extra.start_time} ~ ${extra.end_time}`
  }
  const text = `${item.content ?? ""}`
  const match = text.match(/维护时间[:：]?\s*([^\n]+)/)
  return match?.[1]?.trim() ?? ""
}

function parseMaintenanceItems(item: NoticeItem) {
  const extra = item.extra ?? {}
  const fromExtra = parseStringList(
    extra.maintenance_items ?? extra.content_list ?? extra.items,
  )
  if (fromExtra.length) return fromExtra

  const text = `${item.content ?? ""}`
  const sectionMatch = text.match(/维护内容[:：]?\s*([\s\S]*?)(?=注意事项|$)/)
  if (sectionMatch?.[1]) {
    const parsed = parseStringList(sectionMatch[1])
    if (parsed.length) return parsed
  }

  const lines = parseStringList(text).filter((line) => !line.includes("维护时间"))
  if (lines.length) return lines

  return [
    "系统性能优化",
    "安全补丁更新",
    "数据库维护",
    "新功能部署",
  ]
}

function parseMaintenanceNotice(item: NoticeItem) {
  const extra = item.extra ?? {}
  if (extra.notice) return String(extra.notice)
  if (extra.attention) return String(extra.attention)
  if (extra.notes) return String(extra.notes)

  const text = `${item.content ?? ""}`
  const match = text.match(/注意事项[:：]?\s*([\s\S]+)/)
  return match?.[1]?.trim() || DEFAULT_NOTICE
}

export function isMaintenanceNotice(item: NoticeItem) {
  if (Number(item.type) !== 5) return false
  const text = `${item.label ?? ""}${item.content ?? ""}`
  const extra = item.extra ?? {}
  return (
    text.includes("维护") ||
    extra.maintenance_time != null ||
    extra.time_range != null ||
    extra.maintenance_items != null ||
    extra.content_list != null
  )
}

type Props = {
  item: NoticeItem
  onClose: () => void
  onMarkRead: () => void
  showMarkRead?: boolean
}

const NoticeMaintenanceCard: React.FC<Props> = ({
  item,
  onClose,
  onMarkRead,
  showMarkRead = false,
}) => {
  const theme = useTheme()
  const title = item.label || "系统维护通知"
  const maintenanceTime = useMemo(() => parseMaintenanceTime(item), [item])
  const maintenanceItems = useMemo(() => parseMaintenanceItems(item), [item])
  const noticeText = useMemo(() => parseMaintenanceNotice(item), [item])
  const isRead = item.is_read === true

  return (
    <Card height="auto">
      <CardBody>
        <StartFlex width="100%" alignItems="flex-start" gap="10px">
          <InfoIcon>
            <IconFont name="xttz" size={18} color={theme.colors.blockBackground} />
          </InfoIcon>
          <ColumnFlex flex={1} minWidth={0} height="auto">
            <Text fontSize="18px" fontWeight="600" color={theme.colors.text}>
              {title}
            </Text>
            <StartFlex mt="8px" gap="12px" flexWrap="wrap">
              <StartFlex width="auto">
                <IconFont name="sssj" size={12} color={theme.colors.secondaryText} />
                <Text fontSize="12px" color={theme.colors.secondaryText} ml="6px">
                  {formatMessageTime(item.created)}
                </Text>
              </StartFlex>
              <StartFlex width="auto">
                {isRead ? (
                  <IconFont name="szyx1" size={12} color={theme.colors.primary} />
                ) : (
                  <CenterFlex width="8px" height="8px" bg={theme.colors.primaryText} borderRadius="4px" />
                )}
                <Text
                  fontSize="12px"
                  color={isRead ? theme.colors.primary : theme.colors.primaryText}
                  ml="6px"
                >
                  {isRead ? "已读" : "未读"}
                </Text>
              </StartFlex>
            </StartFlex>
          </ColumnFlex>
        </StartFlex>

        {maintenanceTime ? (
          <TimeBox height="auto">
            <Text fontSize="13px" fontWeight="600" color="#4e98f3">
              维护时间
            </Text>
            <Text fontSize="14px" color="#6ba8f5" mt="6px">
              {maintenanceTime}
            </Text>
          </TimeBox>
        ) : null}

        <Text fontSize="15px" fontWeight="600" color={theme.colors.text} mt="18px">
          维护内容
        </Text>
        <ColumnFlex width="100%" height="auto" mt="4px">
          {maintenanceItems.map((line, index) => (
            <BulletRow key={`${line}-${index}`}>
              <Text fontSize="14px" color={theme.colors.text} lineHeight="22px">
                •
              </Text>
              <Text flex={1} fontSize="14px" color={theme.colors.text} lineHeight="22px">
                {line}
              </Text>
            </BulletRow>
          ))}
        </ColumnFlex>

        <NoticeBox height="auto">
          <Text fontSize="14px" fontWeight="600" color="#9a3412">
            注意事项
          </Text>
          <Text fontSize="13px" color="#9a3412" mt="8px" lineHeight="20px">
            {noticeText}
          </Text>
        </NoticeBox>
      </CardBody>

      <CardFooter height="auto">
        <Button
          flex={1}
          height="44px"
          borderRadius="8px"
          bg={theme.colors.background}
          onClick={onClose}
        >
          <Text color={theme.colors.text}>关闭</Text>
        </Button>
        {showMarkRead ? (
          <Button
            flex={1}
            height="44px"
            borderRadius="8px"
            bg={theme.colors.primary}
            onClick={onMarkRead}
          >
            <Text color={theme.colors.blockBackground}>标记已读</Text>
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  )
}

export default NoticeMaintenanceCard
